/**
 * Tagging for design-tab concepts (design §16). Two storage worlds, one convention:
 * - JSON artifacts (Levels, Screens): a `tags?: string[]` field.
 * - Markdown modular items (Systems / Art Direction / Content): a standalone `Tags: a, b`
 *   line inside the item's body — it round-trips through parseItems/serializeItems as
 *   ordinary prose and rides along automatically in every AI design dump.
 */

const TAGS_LINE_RE = /^tags:\s*(.*)$/i;

export function parseTagList(text: string): string[] {
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Extract the first `Tags:` line from an item body. Returns tags + the body without it. */
export function extractTags(body: string): { tags: string[]; rest: string } {
  const lines = body.split('\n');
  const idx = lines.findIndex((l) => TAGS_LINE_RE.test(l.trim()));
  if (idx === -1) return { tags: [], rest: body };
  const m = TAGS_LINE_RE.exec(lines[idx].trim());
  const tags = parseTagList(m?.[1] ?? '');
  const rest = [...lines.slice(0, idx), ...lines.slice(idx + 1)]
    .join('\n')
    .replace(/^\n+/, '')
    .replace(/\n{3,}/g, '\n\n');
  return { tags, rest };
}

/** Re-write an item body with the given tags as its leading `Tags:` line (or none). */
export function withTags(body: string, tags: string[]): string {
  const { rest } = extractTags(body);
  if (tags.length === 0) return rest;
  return rest.trim()
    ? `Tags: ${tags.join(', ')}\n\n${rest.replace(/^\n+/, '')}`
    : `Tags: ${tags.join(', ')}`;
}
